import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { TemplateWrapper } from "./styles";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import { getFieldMap } from "../../../../utils/inputs";
import { branch, stem, flower } from "./constants";
import { getWeddingDate } from "../../../../utils/formatDate";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import CountDown from "../CountDown";
import AddIcon from "@mui/icons-material/Add";
import MapComponent from "../Components/MapComponent/MapComponent";
import MapButton from "../Components/MapButton";
import Comments from "../Components/Comments";
import InvitationGallery from "../../../InvitationGallery";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";

const Template04 = forwardRef(
	(
		{
			data,
			gallery,
			colorMatrix,
			canEdit = false
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
			setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
		},
		ref
	) => {
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const invitationId = data?._id || "";
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const fieldMap = getFieldMap(invitationId);
		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);

		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitation_type_title = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.the_engagement_of;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.the_katb_ktab_of;
				}
				default: {
					return lang.the_wedding_of;
				}
			}
		}, [data]);

		const invitation_getting_text = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.GETTING_ENGAGED;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.GETTING_KATB_KTAB;
				}
				default: {
					return lang.GETTING_MARRIED;
				}
			}
		}, [data]);

		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);

		return (
			<>
				<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					{/*/////////// section 01 //////////////*/}
					<Box className={"section_01"}>
						<Box className={"section_content"}>
							<Box className={"branch_01_01_wrapper"}>
								<img
									className={"branch_01_01_img elegant_drift"}
									src={branch}
									id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
									alt="branch"
								/>
							</Box>
							<Box className={"stem_01_01_wrapper"}>
								<img className={"stem_01_01_img breeze_animation"} src={stem} alt="stem" />{" "}
							</Box>
							<Box className={"stem_01_02_wrapper"}>
								<img className={"stem_01_02_img breeze_animation"} src={stem} alt="stem" />{" "}
							</Box>
							<Box className={"save_the_date_wrapper"}>
								<Typography className={"wedding_of"}>{invitation_type_title}</Typography>
								{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
									<img
										className={`partner_img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
										data-dispatch-id={fieldMap.GROOM_IMAGE}
										src={data?.groom_image}
										alt="groom"
									/>
								)}

								<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text guide_edit_field"}>
									{data?.groom_name}
								</Typography>

								<PartnersSocialList
									socials={data?.partner_socials_1 || []}
									canEdit={canEdit}
									dispatchId={fieldMap.SOCIAL_MEDIA_1}
								/>
								<img className={"save_date_flower breathing"} src={flower} alt="flower" />
								{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
									<img
										data-dispatch-id={fieldMap.BRIDE_IMAGE}
										className={`partner_img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
										src={data?.bride_image}
										alt="bride"
									/>
								)}

								<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
									{data?.bride_name}
								</Typography>

								<PartnersSocialList
									socials={data?.partner_socials_2 || []}
									canEdit={canEdit}
									dispatchId={fieldMap.SOCIAL_MEDIA_2}
								/>

								<Box data-dispatch-id={fieldMap.WEDDING_DATE} className={"date_wrapper"}>
									<Typography className={"date"}>
										{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate, "MMMM")}
									</Typography>
									<Box
										display={"flex"}
										alignItems={"center"}
										gap={1}
										mt={2}
										className={data?.language === LANG_AR ? RTL : ""}
									>
										<Typography className={"date"}>
											{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate, "EEE")}
										</Typography>
										<Divider
											orientation="vertical"
											sx={{
												borderRightWidth: 3
											}}
										/>
										<Typography className={"date"}>
											{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate, "dd")}
										</Typography>
										<Divider
											orientation="vertical"
											sx={{
												borderRightWidth: 3
											}}
										/>
										<Typography className={"date"}>
											{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate, "yyyy")}
										</Typography>
									</Box>
									<Typography style={{ marginTop: "5px" }} className={"date"}>
										{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate, "p")}
									</Typography>
								</Box>
								{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
									<Typography
										data-dispatch-id={fieldMap.SAVE_THE_DATE}
										className={`info ${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""}`}
									>
										{data?.save_the_date_text}
									</Typography>
								)}
								<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
							</Box>
							<Box className={"branch_01_02_wrapper"}>
								<img className={"branch_01_02_img gentle_sway"} src={branch} alt="branch" />
							</Box>
						</Box>
					</Box>
					{/*/////////// section 02 //////////////*/}
					<Box className={"section_02"}>
						<Typography className={"title"}>{invitation_getting_text}</Typography>
						<Box className={"count_down_wrapper"}>
							<CountDown
								data={data || ({} as Invitations)}
								daysString={lang.days}
								hoursString={lang.hours}
								minutesString={lang.mins}
								secondsString={lang.sec}
							/>
						</Box>
					</Box>
					{/*/////////// section 03 //////////////*/}
					{data?.is_islamic && (
						<Box className={"section_03"}>
							<Typography className={"quran_start"}>بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيمِِ</Typography>
							<Typography className={"quran"}>
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُونَ ﴾
							</Typography>
							<Typography className={"quran_end"}>صَدَقَ اللهُ العَظِيمُ</Typography>
						</Box>
					)}
					{/*/////////// section 04 //////////////*/}
					<Box className={"section_04"}>
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<Box
								data-dispatch-id={fieldMap.MAIN_IMAGE}
								className={`partner_wrapper ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
							>
								<img src={data?.main_image} alt="couple" className={"partner"} id={"main_image"} />
							</Box>
						)}
						<Box className={"initials_wrapper"}>
							<Box className={"top_stems"}>
								<img src={stem} className={"top_left"} alt="stem" />
								<img className={"section_04_flower breathing"} src={flower} alt="flower" />
								<img src={stem} className={"top_right"} alt="stem" />
							</Box>
							<Box className={"initials"}>
								<Box maxWidth={"100px"} style={{ marginRight: "auto" }} display={"flex"} flexDirection={"column"}>
									<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_initial"}>
										{data?.groom_name.charAt(0)}
									</Typography>
								</Box>
								<Box className={"divider_wrapper"}>
									<Divider
										sx={{
											borderTopWidth: 2,
											borderColor: "#426a47"
										}}
									/>
								</Box>
								<Box maxWidth={"100px"} style={{ marginLeft: "auto" }}>
									<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_initial"}>
										{data?.bride_name.charAt(0)}
									</Typography>
								</Box>
							</Box>
							<Box className={"bottom_stems"}>
								<img src={stem} className={"bottom_left"} alt="stem" />
								<Box className={"section_04_flower_wrapper"}>
									<img className={"section_04_flower breathing"} src={flower} alt="flower" />
								</Box>
								<img src={stem} className={"bottom_right"} alt="stem" />
							</Box>
						</Box>
						<Box height={20} />
						{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
							<>
								<Typography
									data-dispatch-id={fieldMap.INTRO_TEXT}
									className={`main_intro ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data?.intro_text}
								</Typography>

								<Box height={20} />
							</>
						)}
						{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
							<>
								<Typography
									data-dispatch-id={fieldMap.SUB_INTRO}
									className={`main_sub_intro ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.sub_intro_text}
								</Typography>
								<Box height={20} />
							</>
						)}
					</Box>

					{/*/////////// section 05 //////////////*/}
					{!data?.fieldVisibility?.loveStory?.hidden && (
						<Box className={"section_05"}>
							<Typography className={"section_title"}>{lang.our_love_story}</Typography>
							{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
								<Box className={`love_story_wrapper ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}>
									<img
										data-dispatch-id={fieldMap.STORY_IMAGE}
										src={data?.story_image}
										className="story_image"
										alt="Love Story"
									/>
								</Box>
							)}
							{data?.story.length > 0 && (
								<Box className={"stories"} data-dispatch-id={fieldMap.STORY}>
									{data?.story.map((story, index) => (
										<Box key={story._id} className={`story animate-on-scroll delay-${index + 1}`}>
											<Typography>{story.title}</Typography>
											<Divider />
											<Typography>{story.description}</Typography>
										</Box>
									))}
								</Box>
							)}
							{canEdit && (data?.story || []).length === 0 && (
								<Box my={3}>
									<Box className={"add_story"} data-dispatch-id={fieldMap.STORY}>
										<AddIcon data-id={`${fieldMap.STORY}-add`} />
									</Box>
								</Box>
							)}
						</Box>
					)}
					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<Box className={"section_09"}>
							<Typography className={"section_title"}>{lang.our_gallery}</Typography>
							<Box height={30}></Box>
							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</Box>
					)}
					{/*/////////// section 06 //////////////*/}
					{!data?.fieldVisibility?.comments?.hidden && (
						<Box className={"section_06"} ref={commentsSectionRef}>
							{!data?.fieldVisibility?.gallery?.hidden && <Box height={30} />}
							<Typography className={"section_title"}>{lang.comments}</Typography>
							{/*<img src={stem} className={"stem"} alt="flower stem" />*/}
							<Box height={30}></Box>

							{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
								<>
									<Typography
										data-dispatch-id={fieldMap.COMMENTS_TEXT}
										className={`comment_text ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
									>
										{data?.comments_text}
									</Typography>
									<Box height={30}></Box>
								</>
							)}
							{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
								<>
									<Typography
										data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
										className={`comment_sub_text ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
									>
										{data?.comments_sub_text}
									</Typography>
									<Box height={30}></Box>
								</>
							)}

							<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
						</Box>
					)}
					{/*/////////// section 07 //////////////*/}
					<Box className={"section_07"} ref={locationSectionRef}>
						{!data?.fieldVisibility?.location?.hidden && (
							<>
								<Box className={"title_wrapper"}>
									<Typography className={"section_title"}>{invitation_location_header}</Typography>
									<Box className={"stem_01_wrapper"}>
										<img src={branch} className={"stem_01 gentle_sway"} alt="branch" />
									</Box>
									<Box className={"stem_02_wrapper"}>
										<img src={branch} className={"stem_02 gentle_sway"} alt="branch" />
									</Box>
								</Box>

								<Box height={20} />
								{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
									<Typography
										data-dispatch-id={fieldMap.LOCATION_TEXT}
										className={`location_text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
									>
										{data?.wedding_location_text}
									</Typography>
								)}
								<Box height={20} />
								<Box data-dispatch-id={fieldMap.MAP}>
									<MapComponent canEdit={canEdit} />
								</Box>

								<Box height={20} />
								<Box data-dispatch-id={fieldMap.MAP}>
									<MapButton canEdit={canEdit} text={lang.location} />
								</Box>
							</>
						)}

						<Box height={30} />

						<Box
							my={3}
							display="flex"
							gap={1}
							alignItems={"center"}
							className={`${data?.language === LANG_AR && RTL} animate-on-scroll delay-1`}
							flexWrap={"wrap"}
						>
							<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text"}>
								{data?.groom_name}
							</Typography>

							<Typography className={"partners_text"}> {lang.and} </Typography>

							<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
								{data?.bride_name}
							</Typography>
						</Box>

						{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
							<Typography
								data-dispatch-id={fieldMap.CONCLUSION_TEXT}
								className={`conclusion_text ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
							>
								{data?.conclusion_text}
							</Typography>
						)}
					</Box>
					{/*/////////// section 08 //////////////*/}
					<Footer colormatrix={colorMatrix} />
				</TemplateWrapper>
			</>
		);
	}
);

export default Template04;
